import type { SxProps, Theme } from '@mui/material';
import { GREEN, NEUTRAL, RED } from 'theme/prismDesignTokens';

// Default / bounds for the drag-resizable panel width. Default is ~150px wider
// than the prior fixed 320 so the AI commentary has room without dragging.
export const DEFAULT_REVIEW_PANEL_WIDTH = 470;
export const MIN_REVIEW_PANEL_WIDTH = 280;
export const MAX_REVIEW_PANEL_WIDTH = 640;

export const clampReviewPanelWidth = (width: number) =>
  Math.min(Math.max(width, MIN_REVIEW_PANEL_WIDTH), MAX_REVIEW_PANEL_WIDTH);

// Drag-resizable side panel that stays mounted across drawer tab changes. The
// live width is applied inline (see ReviewSidePanel); this keeps the static rules.
export const panelSx: SxProps<Theme> = {
  position: 'relative',
  minWidth: MIN_REVIEW_PANEL_WIDTH,
  flexShrink: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.default',
};

// Vertical drag handle straddling the panel's right edge. Mirrors the drawer's
// DrawerSizeBar (row-resize) on the X axis: col-resize, full height, subtle hover.
export const resizeHandleSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  right: -2,
  width: 6,
  height: '100%',
  cursor: 'col-resize',
  zIndex: 1,
  '&:hover': { backgroundColor: 'divider' },
};

// Header is now a flex row: the "Pending Review:" label stays left while the
// compact bulk controls (shown only with a selection) sit right-aligned.
export const headerSx: SxProps<Theme> = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 1.5,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

export const headerTitleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.875rem',
  whiteSpace: 'nowrap',
};

// Compact accept/decline buttons that live inline in the header. Filled with
// white text (variant="contained") to match the app's primary Save button, just
// scaled down - not the prior outlined/no-fill style that read as secondary.
export const bulkActionButtonSx = (color: 'accept' | 'decline'): SxProps<Theme> => ({
  minWidth: 0,
  px: 1,
  py: 0.25,
  fontSize: '0.7rem',
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'none',
  boxShadow: 'none',
  textWrap: 'nowrap',
  color: NEUTRAL.white,
  backgroundColor: color === 'accept' ? GREEN[500] : RED[600],
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: color === 'accept' ? GREEN[600] : RED[700],
  },
});

// Tiny note-toggle / clear icon buttons kept visually subordinate in the header.
export const headerIconButtonSx: SxProps<Theme> = {
  p: 0.25,
  color: 'text.secondary',
};

// Header filter trigger: subordinate at rest, brand-colored while a filter is
// active so it reads as an applied filter rather than a dormant control.
export const filterButtonSx = (active: boolean): SxProps<Theme> => ({
  p: 0.25,
  color: active ? 'primary.main' : 'text.secondary',
});

// Compact header select-all checkbox (selects only the currently-visible pending
// cards). Shrunk to sit inline with the small header icons.
export const selectAllCheckboxSx: SxProps<Theme> = {
  p: 0.25,
  color: 'text.secondary',
};

// Non-interactive section heading inside the filter menu ("Security" / "Metric").
export const filterSectionLabelSx: SxProps<Theme> = {
  px: 1.5,
  pt: 0.75,
  pb: 0.25,
  color: 'text.secondary',
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

// Compact checkable row inside the filter menu.
export const filterMenuItemSx: SxProps<Theme> = {
  py: 0.25,
  '& .MuiCheckbox-root': { p: 0.5, mr: 0.5 },
  '& .MuiTypography-root': { fontSize: '0.8rem' },
};

// Compact bulk note field revealed under the header on demand (collapsed by default).
export const bulkNoteFieldSx: SxProps<Theme> = {
  px: 1.5,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: 'divider',
  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
};

export const listSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  // Only the vertical axis should ever scroll. Setting overflowY:auto alone makes
  // the browser compute overflowX as `auto` too (CSS Overflow: a non-visible value
  // on one axis forces the other off `visible`), which floats a stray horizontal
  // scrollbar whenever the vertical scrollbar's appearance shaves a sub-pixel off
  // the content box. Pinning overflowX:hidden kills that phantom bar while leaving
  // the intended vertical scroll untouched.
  overflowY: 'auto',
  overflowX: 'hidden',
  px: 1.5,
  py: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

export const emptyStateSx: SxProps<Theme> = {
  px: 1.5,
  py: 2,
  color: 'text.secondary',
  fontSize: '0.8rem',
};

// Subtle divider/header introducing the muted "Reviewed" section at the bottom.
export const sectionDividerSx: SxProps<Theme> = {
  mt: 1.5,
  pt: 1.5,
  borderTop: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};
